import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Note } from "@/components/Note";
import { CopyIcon, useCopy } from "@/components/copy";
import type { ApiOrder } from "@/lib/api";

// Платёжный QR по ГОСТ Р 56042-2014 (ST00012 = UTF-8). Банковское приложение сканирует и само
// подставляет реквизиты + сумму. Регистрация в СБП/НСПК не нужна — это стандартная упаковка
// тех же реквизитов, что показаны текстом. Sum — в копейках.
function gostPaymentPayload(order: ApiOrder): string | null {
  const r = order.requisites;
  if (!r || !r.settlement_account || !r.bik) return null;
  const clean = (s: string | null | undefined) => (s ?? "").replace(/\|/g, " ").trim();
  const fields: [string, string][] = [
    ["Name", clean(r.recipient_name)],
    ["PersonalAcc", clean(r.settlement_account)],
    ["BankName", clean(r.beneficiary_bank)],
    ["BIC", clean(r.bik)],
    ["CorrespAcc", clean(r.corr_account)],
    ["PayeeINN", clean(r.recipient_inn)],
    ["KPP", clean(r.recipient_kpp)],
    ["Sum", order.amount_rub > 0 ? String(Math.round(order.amount_rub * 100)) : ""],
    ["Purpose", clean(r.purpose).slice(0, 210)],
  ];
  return "ST00012|" + fields.filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join("|");
}

function PaymentQr({ payload }: { payload: string }) {
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(payload, { width: 260, margin: 1, errorCorrectionLevel: "M", color: { dark: "#0C1013", light: "#ffffff" } })
      .then((u) => alive && setQr(u))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [payload]);
  if (!qr) return null;
  return (
    <div>
      <div className="mb-2 text-[13px] font-medium text-white/55">Оплата по QR</div>
      <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-white/[0.06] bg-surface-card p-4">
        <div className="rounded-xl bg-white p-3 shadow-lg shadow-black/40">
          <img src={qr} alt="QR оплаты" width={220} height={220} className="h-[220px] w-[220px]" />
        </div>
        <div className="text-center text-[12px] leading-snug text-white/45">
          Наведите камеру банковского приложения — реквизиты и сумма подставятся автоматически
        </div>
      </div>
    </div>
  );
}

// Строка реквизита с копированием по тапу. mono — моноширинный для номеров (счета/ИНН/БИК),
// обычный — для наименований.
function CopyRow({ label, value, mono = true }: { label: string; value: string; mono?: boolean }) {
  const { copied, copy } = useCopy(value);
  return (
    <button onClick={copy} className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-white/[0.03]">
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-white/45">{label}</div>
        <div className={`truncate text-[14px] ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {copied ? (
        <span className="shrink-0 text-[12px] font-medium text-emerald-300">Скопировано</span>
      ) : (
        <span className="shrink-0 text-brand">
          <CopyIcon />
        </span>
      )}
    </button>
  );
}

const Sep = () => <div className="mx-4 h-px bg-white/[0.06]" />;

// Реквизиты для оплаты заявки + назначение платежа (экран заявки, пока ждёт оплату).
export function RequisitesBlock({ order }: { order: ApiOrder }) {
  const req = order.requisites;
  const purpose = useCopy(req?.purpose ?? "");
  const qrPayload = useMemo(() => gostPaymentPayload(order), [order]);
  if (!req) return null;
  return (
    <div className="space-y-5">
      {qrPayload && <PaymentQr payload={qrPayload} />}
      <div>
        <div className="mb-2 text-[13px] font-medium text-white/55">Реквизиты получателя</div>
        <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-surface-card">
          <CopyRow label="Получатель" value={req.recipient_name} mono={false} />
          <Sep />
          <CopyRow label="ИНН" value={req.recipient_inn} />
          <Sep />
          <CopyRow label="КПП" value={req.recipient_kpp} />
          <Sep />
          <CopyRow label="Банк получателя" value={req.beneficiary_bank} mono={false} />
          <Sep />
          <CopyRow label="БИК" value={req.bik} />
          <Sep />
          <CopyRow label="Корреспондентский счёт" value={req.corr_account} />
          <Sep />
          <CopyRow label="Расчётный счёт" value={req.settlement_account} />
        </div>
      </div>

      <div>
        <div className="mb-2 text-[13px] font-medium text-white/55">Назначение платежа</div>
        <button
          onClick={purpose.copy}
          className="flex w-full items-start gap-3 rounded-2xl border border-brand/25 bg-brand/[0.06] px-4 py-3.5 text-left active:opacity-70"
        >
          <span className="min-w-0 flex-1 break-words text-[13px] leading-relaxed">{req.purpose}</span>
          {purpose.copied ? (
            <span className="shrink-0 text-[12px] font-medium text-emerald-300">✓</span>
          ) : (
            <span className="shrink-0 text-brand">
              <CopyIcon />
            </span>
          )}
        </button>
      </div>

      <Note variant="warning">
        Укажите <b>назначение платежа</b> и переведите <b>точную сумму</b> — иначе зачисление задержится.
      </Note>
    </div>
  );
}
